import type { MvcComponentData } from '@/cms/types'
import type { SitecoreBreadcrumb } from './fields/types/common'
import type {
  SitecoreContentblock,
  SitecoreContentUspItemFields,
  SitecoreDestinationBlock,
  SitecoreInspiringImage,
} from './fields/types/content-page'
import type { SitecoreProductAdsFieldData } from './fields/types/home-page'
import type { SitecoreMealplansFieldData, SitecoreToKnowFieldData } from './fields/types/product-detail-page'
import {
  BlockContainer,
  Breadcrumb,
  ContentBlock,
  ContentBoxWithImage,
  ContentHeader,
  ContentPageTemplate,
  ContentUsps,
  InspiringImage,
  InspiringImages,
} from '@/react-pages/ContentPage'
import { PriceTable } from '@/react-pages/Prices/PriceTable'
import PromotedPrice from '@/react-pages/Prices/PromotedPrice'
import { Footer } from '@/scopes/common/components'

import { NonCatalogueContent } from '@/scopes/common/components/Footer/NonCatalogueContent'
import { SummerContent } from '@/scopes/common/components/Footer/SummerContent'
import featureFlagClient from '@/scopes/common/infrastructure/feature-flags/featureFlagClient'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { HomeTileProductAds } from '@/scopes/product-ads/components/HomeTile'
import { ProductAdsBanner } from '@/scopes/product-ads/components/ProductAdsBanner'
import {
  AccommodationGallery,
  AnchorMenu,
  Description,
  DestinationSection,
  Facilities,
  GoodToKnow,
  Mealplans,
  PDPHeading,
  PracticalInformation,
  RecommendedAccos,
  RoomTypes,
  UserReviews,
} from '@/scopes/products/pages'
import QuickSearch from '@/scopes/search/quick-search/components/QuickSearch'
import SearchPage from '@/scopes/search/search/pages/SearchPage/SearchPage'
import * as React from 'react'
import { Header } from '../scopes/common/components/Header'
import { AnchorHref, AnchorTarget } from '../scopes/products/pages/components/AnchorMenu'
import { CatalogueType } from './catalogueTypeHelper'

// Register React components here if you migrate from MVC to React
export function getComponentIndex(catalogueType: CatalogueType): Record<string, unknown> {
  const componentsIndex: Record<string, unknown> = {
    SearchBlock: () => <SearchPage />,
    PromotedPrice: () => <PromotedPrice />,
    BookingGate: () => <PriceTable />,
    AccommodationMealplans: (data: MvcComponentData<SitecoreMealplansFieldData>) => (
      <NewRelicErrorBoundary scope="accommodation-page" fallback={null}>
        <Mealplans title={data.renderingContext.item.fields.mealplansheadertitle} />
      </NewRelicErrorBoundary>
    ),
    Header: () => <Header hideLiveSearch={catalogueType === CatalogueType.None} />,
    Footer: () => (
      <Footer>
        {catalogueType === CatalogueType.Summer && <SummerContent />}
        {catalogueType === CatalogueType.None && <NonCatalogueContent />}
      </Footer>
    ),
    AccommodationCarousel: () => <AccommodationGallery />,
    AccommodationDescription: () => <Description />,
    AccomodationTitleReactDescription: () => (
      <AnchorTarget anchorHref={AnchorHref.General}>
        <PDPHeading />
      </AnchorTarget>
    ),
    AccomodationTitleReactPriceTable: () => <PDPHeading />,
    AccommodationTitle: () => <></>,
    AccommodationReviewsBlock: () => <></>,
    AccommodationRatingBlock: () => <></>,
    AnchorNavigation: () => <AnchorMenu />,
    ReviewsTeaser: () => (
      <AnchorTarget anchorHref={AnchorHref.Reviews}>
        <UserReviews />
      </AnchorTarget>
    ),
    AccommodationRoomTypes: () => (
      <AnchorTarget anchorHref={AnchorHref.Roomtypes}>
        <RoomTypes />
      </AnchorTarget>
    ),
    PracticalInformation: () => (
      <AnchorTarget anchorHref={AnchorHref.PracticalInfo}>
        <PracticalInformation />
      </AnchorTarget>
    ),
    AccommodationGoodToKnow: (data: MvcComponentData<SitecoreToKnowFieldData>) => (
      <GoodToKnow
        headingTitle={data.renderingContext.item.fields.headingtitle}
        title={data.renderingContext.item.fields.title}
      />
    ),
    Recommendedaccolister: () => <RecommendedAccos />,
    ProductAds: (data: MvcComponentData<SitecoreProductAdsFieldData>) => (
      <HomeTileProductAds
        contextItemId={data.renderingContext.item.id}
        headerTitle={data.renderingContext.item.fields.title}
        infoLabel={data.renderingContext.item.fields.infotitle}
        infoContent={data.renderingContext.item.fields.infocontent}
      />
    ),
    ProductAdsBanner: () => <ProductAdsBanner componentType="LandingBanner" />,
    DestinationSection: () => (
      <AnchorTarget anchorHref={AnchorHref.Destination}>
        <DestinationSection />
      </AnchorTarget>
    ),
    ContentHeader: () => <ContentHeader />,
    Breadcrumb: (data: MvcComponentData<SitecoreBreadcrumb>) => {
      if (!data.datasource?.BreadcrumbElements)
        return null
      const items = data.datasource.BreadcrumbElements.map(item => ({ text: item.Name, href: item.Url }))
      return <Breadcrumb items={items} />
    },
    ContentBlock: (data: MvcComponentData<SitecoreContentblock>) =>
      data.renderingContext.item && (
        <ContentBlock
          title={data.renderingContext.item.fields.title}
          subTitle={data.renderingContext.item.fields.subtitle}
          bodyText={data.renderingContext.item.fields.text}
        />
      ),
    BlockContainer: (data: any) => <BlockContainer {...data} />,
    ContentPageTemplate: (data: any) => <ContentPageTemplate {...data} />,
    EmergencyBanner: () => <></>, // Pending to implement proper component
    CookieConsent: () => <></>, // Pending to implement proper component
    LoginByUrl: () => <></>, // Pending to implement proper component
    CePartnersWelcomeMessageDialog: () => <></>, // Pending to implement proper component
    DestinationBlock: (data: MvcComponentData<SitecoreDestinationBlock>) =>
      data.renderingContext.item && (
        <ContentBoxWithImage
          title={data.renderingContext.item.fields.destinationblocktitle}
          imageUrl={data.renderingContext.item.fields.destinationblockimage?.url ?? undefined}
          content={data.renderingContext.item.fields.destinationblocklines ?? undefined}
        />
      ),
    AccommodationFacilities: () => (
      <AnchorTarget anchorHref={AnchorHref.Facilities}>
        <Facilities />
      </AnchorTarget>
    ),
    ContentUsps: (data: MvcComponentData<undefined, SitecoreContentUspItemFields>) => {
      if (!data.renderingContext.item)
        return null
      if (!data.renderingContext.item.children)
        return null

      const items = data.renderingContext.item.children.map(item => item.fields.uspcontent)
      return <ContentUsps usps={items} />
    },
    InspiringImage: (data: MvcComponentData<SitecoreInspiringImage>) =>
      data.renderingContext.item && (
        <InspiringImage
          backgroundimage={data.renderingContext.item.fields.backgroundimage}
          maintext={data.renderingContext.item.fields.maintext}
          subtitletext={data.renderingContext.item.fields.subtitletext}
          calltoactionlink={data.renderingContext.item.fields.calltoactionlink}
        />
      ),

    InspiredImagesTwoItems: (data: any) => {
      return <InspiringImages {...data} />
    },
    // InspiredImagesTwoItems: () => <InspiringImages />,
  }

  if (featureFlagClient.isActive('ENABLE_QUICKSEARCH_REACT')) {
    componentsIndex.QuickSearch = () => <QuickSearch catalogueType={catalogueType} />
  }

  return componentsIndex
}
